import { RewardTileType } from '@/components/TileLister'
import { useAppDispatch, useAppSelector, useGetStore } from '@/redux/hooks/hooks'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiAddRewardToWishlist, apiRemoveRewardFromWishlist } from '@vrw/data/src/api/wishlist.api'
import { getRedUserId } from '@vrw/data/src/redux/profile/selectors'
import { doSetToastMessage } from '@vrw/data/src/redux/toast/dispatchers'
import { addRewardToWishlist, removeRewardFromWishlist } from '@vrw/data/src/redux/wishlist/actions'

import { WishedType } from '@vrw/data/src/redux/wishlist/types'
import { getString } from '@vrw/data/src/utils'

const LIMIT = 9
const LIMIT_REACHED_ERROR_CODE = 'FAVOURITES_LIMIT_REACHED'

type FavouritesMutationPayload = {
  tile: RewardTileType
  type: WishedType
}

type FavouritesError = Error & { cause: { code: string } }

export const useFavourites = () => {
  const dispatch = useAppDispatch()
  const redUserId = useAppSelector(getRedUserId)
  const queryClient = useQueryClient()
  const wishlist = useGetStore('wishlist.wishlist')

  const { mutate: addToFavourites } = useMutation({
    mutationFn: async ({ tile, type }: FavouritesMutationPayload) => {
      if (wishlist.rewards.length >= LIMIT) {
        throw new Error('Reached the maximum number of favourites', {
          cause: {
            code: LIMIT_REACHED_ERROR_CODE,
          },
        })
      }

      dispatch(addRewardToWishlist({ rewardId: tile.rewardId, title: tile.title, logoUrl: tile.brandLogoUrl, type }))
      await apiAddRewardToWishlist({ redUserId, rewardId: tile.rewardId, rewardType: type })
    },
    onSuccess: () => {
      dispatch(doSetToastMessage(getString('home.favourites.toast.added'), 'information', false, 3000))
      queryClient.invalidateQueries({ queryKey: ['wishlist'] }) // future-proofing
    },
    onError: (error: FavouritesError) => {
      if (error.cause.code === LIMIT_REACHED_ERROR_CODE) {
        dispatch(doSetToastMessage(getString('home.favourites.toast.limit'), 'information', false, 3000))
      }
    },
  })

  const { mutate: deleteFromFavourites } = useMutation({
    mutationFn: async ({ tile, type }: FavouritesMutationPayload) => {
      dispatch(removeRewardFromWishlist(tile.rewardId))
      await apiRemoveRewardFromWishlist({ redUserId, rewardId: tile.rewardId, rewardType: type })
    },
    onSuccess: () => {
      dispatch(doSetToastMessage(getString('home.favourites.toast.removed'), 'information', false, 6000))
      queryClient.invalidateQueries({ queryKey: ['wishlist'] }) // future-proofing
    },
  })

  return {
    addToFavourites,
    deleteFromFavourites,
  }
}
